import { AKComponent } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart bubble count.
 *
 * @typedef {Object} CartBubbleRefs
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 */

/**
 * @extends {AKComponent<CartBubbleRefs>}
 */
class CartBubbleComponent extends AKComponent {
  /** @type {number} */
  get currentCartCount() {
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    if (!countElement) return 0;

    const fromAttr = countElement.getAttribute('data-cart-count');
    if (fromAttr != null && fromAttr !== '') {
      const n = parseInt(fromAttr, 10);
      if (!Number.isNaN(n)) return n;
    }

    const digits = String(countElement.textContent ?? '').replace(/\D/g, '');
    const parsed = parseInt(digits, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * @param {number} value
   */
  set currentCartCount(value) {
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    if (!countElement) return;

    const safe = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    countElement.setAttribute('data-cart-count', String(safe));

    const isTextOnly = !this.classList.contains('c-cart-bubble');
    if (isTextOnly) {
      countElement.textContent = safe < 100 ? `[${safe}]` : '[99+]';
    } else {
      countElement.textContent = safe < 100 ? String(safe) : '99+';
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event (`cart:update`), including {@link CartAddEvent} and {@link CartUpdateEvent}.
   * @param {CartAddEvent | CartUpdateEvent} event
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    /** `CartAddEvent` always carries the quantity added (delta), not the cart total. */
    const incrementFromAdd = event instanceof CartAddEvent;

    this.renderCartBubble(itemCount, incrementFromAdd);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Cart total after update, or quantity added when `incrementFromAdd` is true.
   * @param {boolean} incrementFromAdd - When true, `itemCount` is added to the current displayed total (add-to-cart flows).
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, incrementFromAdd, animate = true) => {
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    const textElement = this.refs.cartBubbleText || this.querySelector('[ref="cartBubbleText"]');

    const delta = Number(itemCount) || 0;
    const previousTotal = this.currentCartCount;
    const displayTotal = incrementFromAdd ? previousTotal + delta : delta;

    if (countElement) {
      if (displayTotal === 0) {
        countElement.classList.add('u-d-none');
      } else {
        countElement.classList.remove('u-d-none');
      }
    }

    if (displayTotal === 0) {
      this.classList.add('u-visually-hidden');
    } else {
      this.classList.remove('u-visually-hidden');
    }

    if (displayTotal > 0 && animate) {
      this.classList.add('c-cart-bubble--animating');
    } else {
      this.classList.remove('c-cart-bubble--animating');
    }

    this.currentCartCount = displayTotal;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || !textElement) return;
    await onAnimationEnd(textElement);

    this.classList.remove('c-cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const raw = sessionStorage.getItem('cart-count');
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    if (!raw || !countElement) return;

    let sessionValue = NaN;
    try {
      const { value, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp >= 10000) return;
      sessionValue = parseInt(value, 10);
    } catch {
      return;
    }

    if (Number.isNaN(sessionValue) || sessionValue < 0) return;

    if (sessionValue === this.currentCartCount) return;

    this.renderCartBubble(sessionValue, false, false);
  };
}

if (!customElements.get('cart-bubble')) {
  customElements.define('cart-bubble', CartBubbleComponent);
}